import { useBreakpointValue } from "@chakra-ui/react";

import { RegionType } from "graphql/generated";
import { colors as dsColors } from "src/colors";

import { colors } from "./colors";

export const AppTheme = {
  fonts: {
    body: "Body-Grotesque-Fit, Inter, system-ui, sans-serif",
    heading:
      RegionType.Sg === process.env.NEXT_PUBLIC_COUNTRY
        ? "Body-Grotesque-Fit, Ogg, serif"
        : "Body-Grotesque-Fit, Ogg, sans-serif",
    mono: "Body-Grotesque-Fit, Ogg, monospace",
    graphik: "Graphik"
  },
  styles: {
    global: () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const isMobileBreakpoint = useBreakpointValue({ base: true, lg: false });
      return {
        input: { fontSize: "16px" },
        "html, body": {
          fontSize: isMobileBreakpoint ? "sm" : "md",
          color: "#003534",
          bgColor: dsColors.white.off,
          pb: 0
        },

        ".noahDatePicker": {
          bg: "white",
          border: "none",
          "& .react-datepicker__header--custom": {
            bg: "transparent",
            border: "none"
          },
          "& .react-datepicker__day--selected": {
            bg: "transparent",
            color: "primary.500",
            borderColor: "primary.500",
            borderRadius: "100%"
          },
          "& .react-datepicker__month": {
            m: 0
          }
        },
        ".reactDay": {
          fontFamily: "body",
          color: "textColor",
          m: 2,
          border: "1px solid",
          borderColor: "transparent",
          "&.react-datepicker__day--keyboard-selected": {
            bg: "white",
            border: "1px solid",
            borderColor: "primary.500",
            borderRadius: "100%",
            color: "primary.500"
          },
          _focus: {
            outline: "none"
          },
          _hover: {
            color: "primary.500",
            bg: "transparent",
            border: "1px solid",
            borderColor: "primary.500",
            borderRadius: "100%"
          }
        },
        ".reactMonth": {
          color: "red"
        },
        ".reactWeekday": {
          fontFamily: "body",
          mx: 2,
          mt: 4,
          textTransform: "uppercase",
          color: "textColor"
        }
      };
    }
  },
  components: {
    FormLabel: {
      variants: {
        "checkout-form": {
          color: "rgba(0, 0, 0, 0.4)",
          fontWeight: 600,
          fontSize: { base: "xs", md: "md" }
        }
      }
    },
    Text: {
      baseStyle: {
        color: "primaryTwo.900"
      },
      variants: {
        subTitle: {
          color: "textColor",
          fontSize: ["12px", "16px"]
        },
        info: {
          color: "textColor",
          lineHeight: 4,
          fontSize: ["12px", "16px"]
        },
        evalQuestion: {
          fontWeight: 700
        }
      }
    },
    Button: {
      variants: {
        primary: {
          width: "max-content",
          border: "2px",
          fontSize: { base: "xs", md: "md" },
          borderColor: "primary.500",
          rounded: "3xl",
          color: "white",
          bg: "primary.500",
          _hover: {
            _disabled: {
              bgColor: "primary.500"
            }
          }
        },
        secondary: {
          width: "max-content",
          border: "2px",
          borderColor: "primary.500",
          rounded: "3xl",
          color: "primary.500",
          fontSize: { base: "xs", md: "md" }
        },
        whiteButton: {
          width: "max-content",
          border: "none",
          rounded: "58px",
          bg: "white",
          color: "primary.500",
          textTransform: "uppercase",
          boxShadow: "md",
          _focus: {
            boxShadow: "md"
          }
        }
      }
    },
    Heading: {
      sizes: {
        md: {
          fontSize: ["20px", "36px"],
          fontWeight: 800
        },
        sm: {
          fontSize: ["14px", "18px"],
          fontWeight: 600
        },
        xs: {
          fontSize: ["8px", "12px"]
        }
      },
      variants: {
        checkoutTitle: {
          fontWeight: 800
        }
      }
    },
    Link: {
      variants: {
        action: {
          textDecor: "underline",
          textTransform: "uppercase",
          fontSize: ["10px", "14px"]
        }
      }
    }
  },

  // https://colors.eva.design/
  colors: {
    default: {
      100: "#F9F6F2",
      200: "#f285597d",
      300: "#ffffff",
      400: "#000000",
      500: "#B3B3B3"
    },
    primary: {
      100: "#F4BD98",
      200: "#F7AB81",
      300: "#F28559",
      400: "#F4733F",
      500: "#EF6630",
      600: "#E4622F",
      700: "#DC5D2B",
      800: "#D35C2D",
      900: "#C85325",
      1000: "#d05322"
    },
    primaryTwo: {
      100: "#77D1AD",
      200: "#68C6A0",
      300: "#4CB58B",
      400: "#47AA82",
      500: "#3D9974",
      600: "#30856B",
      700: "#177265",
      800: "#10625D",
      900: "#003534"
    },
    secondary: {
      100: "#D6D6D6"
    },
    green: {
      dark: "#00341C",
      light: "#C1F9CC",
      50: "#d9fff6",
      100: "#acffe4",
      200: "#7bffd3",
      300: "#49ffc3",
      400: "#1affb3",
      500: "#00e699",
      600: "#00b377",
      700: "#008054",
      800: "#004e31",
      900: "#001c0e"
    },
    textColor: "#003534",
    primary3: "#EF6630",
    primary4: "#3D9974",
    primary5: "#3D9974",
    evalStar: "#F28559",
    reviewerName: "#d05322",
    placeholderInDarkTextBox: "rgba(239, 102, 48, 1)",
    checkoutTitle: "textColor",
    exploreSectionBackground: "#003534",
    exploreSectionTitle: "white",
    lightGrey: "#D6D6D6",
    cashbackBoxBg: "rgba(61, 153, 116, 0.15)",
    checkbox: { 500: "transparent" },
    surveyModal: {
      textColor: "#003534",
      primary: "#EF6630"
    },
    ...colors
  },
  sizes: {
    topBar: "72px",
    sidebar: "220px"
  },
  zIndices: {
    topBar: 900
  }
};
